import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Link,
  makeStyles,
  mergeClasses,
  MessageBar,
  MessageBarBody,
  shorthands,
  Spinner,
  tokens,
} from '@fluentui/react-components';
import {
  AppsAddInRegular,
  ArchiveRegular,
  CheckmarkRegular,
  ClockRegular,
  DataUsageRegular,
  DocumentDataRegular,
  DocumentRegular,
  GiftRegular,
  GridRegular,
  LightbulbRegular,
  ShareRegular,
  ShieldKeyholeRegular,
  ShieldPersonRegular,
} from '@fluentui/react-icons';
import { Shell } from '../components/Shell';
import { DataFlowDiagram } from '../components/DataFlowDiagram';
import { Kicker, Lead, Muted, Title } from '../components/ui';
import { brand } from '../theme';
import { TENANT_BANDS, useSignup, type TenantBand } from '../lib/signupState';
import { forgetMicrosoft } from '../lib/msAuth';
import { content, drawLine, fill } from '../content/content';

// Icon names used in landing.json.
const ICONS: Record<string, ReactNode> = {
  shield: <ShieldKeyholeRegular />,
  clock: <ClockRegular />,
  check: <CheckmarkRegular />,
  sharing: <ShareRegular />,
  storage: <ArchiveRegular />,
  content: <DocumentRegular />,
  usage: <DataUsageRegular />,
  admin: <ShieldPersonRegular />,
  workloads: <GridRegular />,
  advice: <LightbulbRegular />,
  metadata: <DocumentDataRegular />,
  install: <AppsAddInRegular />,
};

const DARK = '@media (prefers-color-scheme: dark)';

const useStyles = makeStyles({
  // Hero
  trust: { listStyleType: 'none', margin: 0, padding: 0, display: 'grid', gap: '16px' },
  trustItem: { display: 'grid', gridTemplateColumns: '24px 1fr', gap: '12px', alignItems: 'start', color: brand.chromeText },
  trustIcon: { fontSize: '22px', display: 'flex', marginTop: '2px' },
  trustText: { fontSize: '0.92rem' },
  pillars: { listStyleType: 'none', margin: 0, padding: 0, display: 'grid', gap: '10px' },
  pillar: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    gap: '10px',
    alignItems: 'start',
    padding: '10px 12px',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('6px'),
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  pillarIcon: { fontSize: '20px', color: tokens.colorBrandForeground1, marginTop: '1px', display: 'flex' },
  strong: { fontWeight: tokens.fontWeightSemibold, color: 'var(--tellec-heading)' },
  multiTenant: { margin: 0, fontWeight: tokens.fontWeightSemibold, color: 'var(--tellec-heading)' },
  actions: { display: 'grid', gap: '12px' },
  cta: { minHeight: '48px', fontSize: tokens.fontSizeBase400 },
  draw: {
    margin: 0,
    display: 'grid',
    gridTemplateColumns: '20px 1fr',
    gap: '8px',
    alignItems: 'start',
    padding: '10px 12px',
    ...shorthands.borderRadius('6px'),
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  drawIcon: { fontSize: '20px', display: 'flex', color: tokens.colorBrandForeground2 },
  // Phones don't see the brand panel's text, so it is repeated under the card.
  mobileTrust: {
    backgroundColor: brand.chrome,
    color: brand.chromeText,
    padding: '28px 16px',
    display: 'grid',
    gap: '20px',
    '@media (min-width: 861px)': { display: 'none' },
  },
  mobileTrustTitle: { margin: 0, fontSize: '1.4rem', fontWeight: 600, lineHeight: 1.2 },
  mobileTrustLine: { margin: '6px 0 0', opacity: 0.95 },

  // Sections
  section: { padding: '48px 16px', '@media (min-width: 861px)': { padding: '72px 24px' } },
  plain: { backgroundColor: tokens.colorNeutralBackground1 },
  soft: { backgroundColor: tokens.colorNeutralBackground2 },
  tint: { backgroundColor: brand.light, [DARK]: { backgroundColor: '#0a2a63' } },
  navy: { backgroundColor: brand.primary, color: '#ffffff', [DARK]: { backgroundColor: '#0a2a63' } },
  inner: { maxWidth: '1120px', margin: '0 auto', display: 'grid', gap: '28px' },
  h2: {
    margin: 0,
    maxWidth: '820px',
    fontSize: '1.6rem',
    lineHeight: 1.2,
    fontWeight: 700,
    color: 'var(--tellec-heading)',
    '@media (min-width: 861px)': { fontSize: '2.1rem' },
  },
  h2Light: { color: '#ffffff' },
  centre: { textAlign: 'center', marginLeft: 'auto', marginRight: 'auto' },
  intro: { margin: 0, fontSize: '1.1rem', color: tokens.colorNeutralForeground2 },
  // Non-overlapping ranges: Griffel doesn't guarantee the order of media queries.
  grid: { display: 'grid', gap: '16px', gridTemplateColumns: '1fr', '@media (min-width: 640px) and (max-width: 999px)': { gridTemplateColumns: '1fr 1fr' } },
  grid4: { '@media (min-width: 1000px)': { gridTemplateColumns: 'repeat(4, 1fr)' } },
  grid3: { '@media (min-width: 1000px)': { gridTemplateColumns: 'repeat(3, 1fr)' } },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('8px'),
    padding: '20px',
    display: 'grid',
    alignContent: 'start',
    gap: '8px',
  },
  number: { fontSize: '2rem', fontWeight: 700, lineHeight: 1, color: tokens.colorBrandForeground1 },
  cardIcon: { fontSize: '28px', display: 'flex', color: tokens.colorBrandForeground1 },
  h3: { margin: 0, fontSize: '1.15rem', lineHeight: 1.3, fontWeight: 700, color: 'var(--tellec-heading)' },
  p: { margin: 0, color: tokens.colorNeutralForeground2 },

  // Dashboard
  figure: { margin: 0, display: 'grid', gap: '12px' },
  shot: {
    display: 'block',
    width: '100%',
    height: 'auto',
    ...shorthands.borderRadius('8px'),
    boxShadow: '0 12px 32px rgba(7,25,82,0.16), 0 2px 6px rgba(0,0,0,0.08)',
  },
  caption: { textAlign: 'center', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: '12px',
    ...shorthands.border('2px', 'dashed', tokens.colorNeutralStroke1),
    ...shorthands.borderRadius('8px'),
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    fontSize: tokens.fontSizeBase300,
  },
  shotPlaceholder: { aspectRatio: '16 / 9' },

  // Logos
  logos: {
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '24px 40px',
  },
  logo: {
    display: 'block',
    height: '40px',
    width: 'auto',
    maxWidth: '150px',
    objectFit: 'contain',
    filter: 'grayscale(1)',
    opacity: 0.75,
    [DARK]: { filter: 'grayscale(1) brightness(0) invert(1)', opacity: 0.8 },
  },
  logoPlaceholder: { height: '40px', width: '140px', fontSize: tokens.fontSizeBase200, padding: '4px' },

  // Data flow
  blocks: { display: 'grid', gap: '24px', '@media (min-width: 861px)': { gridTemplateColumns: 'repeat(3, 1fr)' } },
  block: { display: 'grid', gridTemplateColumns: '28px 1fr', gap: '12px', alignItems: 'start' },

  // Comparison
  tableWrap: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('8px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ':focus-visible': { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: '2px' },
  },
  table: { width: '100%', minWidth: '720px', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase300 },
  th: {
    textAlign: 'left',
    verticalAlign: 'bottom',
    padding: '14px 16px',
    color: 'var(--tellec-heading)',
    fontWeight: 700,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
  },
  rowHead: { textAlign: 'left', fontWeight: tokens.fontWeightSemibold, color: 'var(--tellec-heading)', width: '20%' },
  td: { padding: '12px 16px', verticalAlign: 'top', borderTop: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground2 },
  ours: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold },
  scrollHint: { margin: 0, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200, '@media (min-width: 761px)': { display: 'none' } },

  // Steps (navy band)
  stepCard: { display: 'grid', gap: '6px', alignContent: 'start' },
  stepNumber: { fontSize: '2rem', fontWeight: 700, lineHeight: 1, color: '#80d1d8' },
  stepTitle: { margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#ffffff' },
  stepText: { margin: 0, color: '#e6ecf5' },
  stepCta: { justifySelf: 'start', minHeight: '48px', minWidth: '220px', fontSize: tokens.fontSizeBase400 },

  // FAQ
  faq: { maxWidth: '820px', width: '100%' },
  faqItem: { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  faqHeader: { '& button': { minHeight: '56px', fontSize: '1.05rem', fontWeight: tokens.fontWeightSemibold, color: 'var(--tellec-heading)' } },
  faqAnswer: { margin: '0 0 16px', color: tokens.colorNeutralForeground2 },

  // Footer
  footer: {
    padding: '28px 16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
  },
  footerInner: {
    maxWidth: '1120px',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px 14px',
  },
  footerLink: { display: 'inline-block', padding: '12px 0', color: tokens.colorNeutralForeground2, textDecorationLine: 'underline' },
  dot: { color: tokens.colorNeutralForeground3 },
  missing: { ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke1), ...shorthands.borderRadius('4px'), padding: '0 6px' },
});

interface LinkResponse {
  state: 'open' | 'consented' | 'closed' | 'invalid' | 'expired';
  details?: { name: string; email: string; mobile: string; mspName: string; tenantBand: string };
}

type LinkStatus = { kind: 'none' } | { kind: 'loading' } | { kind: 'welcome'; firstName: string } | { kind: 'problem'; message: string };

const LINK_PROBLEMS: Record<string, string> = {
  invalid: "That link didn't work. You can still claim your NFR below.",
  expired: 'That link has expired. You can still claim your NFR below.',
  closed: 'That link is no longer active. You can still claim your NFR below.',
  error: "We couldn't load your details just now. You can fill them in below.",
};

/** Personalised link (?t=): look up the deal and pre-fill the details screen. */
function usePersonalLink(): LinkStatus {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { state, update, reset } = useSignup();
  const t = params.get('t');
  const fromLink = Boolean(state.token && state.details && !state.captured);
  const [status, setStatus] = useState<LinkStatus>(() =>
    t ? { kind: 'loading' } : fromLink ? { kind: 'welcome', firstName: firstName(state.details!.name) } : { kind: 'none' },
  );

  useEffect(() => {
    // Back at the start after a capture: someone new (e.g. a shared tablet at the table).
    if (!t && state.captured) {
      reset();
      void forgetMicrosoft();
    }
    if (!t) return;
    let cancelled = false;
    // Drop the token from the address bar so it isn't shared by accident.
    setParams({}, { replace: true });
    fetch(`/api/link?t=${encodeURIComponent(t)}`, { cache: 'no-store' })
      .then(async (res) => ({ ok: res.ok || res.status === 400 || res.status === 410, body: (await res.json().catch(() => ({}))) as LinkResponse }))
      .then(({ ok, body }) => {
        if (cancelled) return;
        if (!ok) throw new Error('lookup failed');
        if (body.state === 'consented') {
          navigate('/done?status=already', { replace: true });
          return;
        }
        if (body.state !== 'open' || !body.details) {
          reset();
          setStatus({ kind: 'problem', message: LINK_PROBLEMS[body.state] ?? LINK_PROBLEMS.error });
          return;
        }
        const d = body.details;
        reset();
        update({
          token: t,
          details: {
            name: d.name,
            email: d.email,
            mobile: d.mobile,
            mspName: d.mspName,
            jobTitle: '',
            tenantBand: (TENANT_BANDS as readonly string[]).includes(d.tenantBand) ? (d.tenantBand as TenantBand) : '',
            heardFrom: 'Kaseya Connect',
            marketingOptIn: false,
          },
        });
        setStatus({ kind: 'welcome', firstName: firstName(d.name) });
      })
      .catch(() => {
        if (!cancelled) setStatus({ kind: 'problem', message: LINK_PROBLEMS.error });
      });
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return status;
}

function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? '';
}

/** "0400 000 000" → "+61400000000" for tel: links. */
function telHref(mobile: string): string {
  const digits = mobile.replace(/[^\d+]/g, '');
  return `tel:${digits.startsWith('0') ? '+61' + digits.slice(1) : digits}`;
}

function Section({ tone, labelledBy, label, children }: { tone: 'plain' | 'soft' | 'tint' | 'navy'; labelledBy?: string; label?: string; children: ReactNode }) {
  const s = useStyles();
  return (
    <section className={mergeClasses(s.section, s[tone])} aria-labelledby={labelledBy} aria-label={label}>
      <div className={s.inner}>{children}</div>
    </section>
  );
}

export function Landing() {
  const s = useStyles();
  const navigate = useNavigate();
  const link = usePersonalLink();
  const welcome = link.kind === 'welcome';
  const loading = link.kind === 'loading';
  const draw = drawLine();
  const { hero, dashboard, logos, sprawl, features, dataFlow, comparison, steps, faq, footer } = content;

  const claim = (label: string, className: string) => (
    <Button
      appearance="primary"
      size="large"
      className={className}
      disabledFocusable={loading}
      icon={loading ? <Spinner size="tiny" appearance="inverted" /> : undefined}
      onClick={() => navigate('/details')}
    >
      {loading ? 'Loading your details…' : label}
    </Button>
  );

  const trustList = (
    <ul className={s.trust}>
      {hero.trust.map((v) => (
        <li key={v.title} className={s.trustItem}>
          <span className={s.trustIcon} aria-hidden="true">
            {ICONS[v.icon]}
          </span>
          <span className={s.trustText}>
            <strong>{v.title}</strong> {fill(v.text)}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <>
      <Shell labelledBy="title" brandTitle={hero.subtitle} brandTagline={hero.line} brandExtra={trustList} hideFooter>
        <div>
          <Kicker>{hero.eyebrow}</Kicker>
          <Title id="title">{welcome && link.firstName ? fill(hero.welcomeHeading, { firstName: link.firstName }) : hero.heading}</Title>
        </div>
        {link.kind === 'problem' && (
          <MessageBar intent="warning">
            <MessageBarBody>{link.message}</MessageBarBody>
          </MessageBar>
        )}
        <Lead>{hero.intro}</Lead>
        <ul className={s.pillars} aria-label={hero.pillarsLabel}>
          {hero.pillars.map((p) => (
            <li key={p.title} className={s.pillar}>
              <span className={s.pillarIcon} aria-hidden="true">
                {ICONS[p.icon]}
              </span>
              <span>
                <strong className={s.strong}>{p.title}</strong> {fill(p.text)}
              </span>
            </li>
          ))}
        </ul>
        <p className={s.multiTenant}>{hero.multiTenant}</p>
        <div className={s.actions}>
          {claim(hero.button, s.cta)}
          <Muted>{welcome ? hero.welcomeSmallPrint : hero.smallPrint}</Muted>
          {draw && (
            <p className={s.draw}>
              <span className={s.drawIcon} aria-hidden="true">
                <GiftRegular />
              </span>
              <span>{draw}</span>
            </p>
          )}
        </div>
      </Shell>

      <section className={s.mobileTrust} aria-label={hero.subtitle}>
        <div>
          <p className={s.mobileTrustTitle}>{hero.subtitle}</p>
          <p className={s.mobileTrustLine}>{hero.line}</p>
        </div>
        {trustList}
      </section>

      {/* 1. Dashboard */}
      <Section tone="soft" label={dashboard.caption}>
        <figure className={s.figure}>
          {dashboard.src ? (
            <img
              className={s.shot}
              src={dashboard.src}
              alt={dashboard.alt}
              width={dashboard.width}
              height={dashboard.height}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className={mergeClasses(s.placeholder, s.shotPlaceholder)} role="img" aria-label={dashboard.alt}>
              {dashboard.placeholder}
            </div>
          )}
          <figcaption className={s.caption}>{dashboard.caption}</figcaption>
        </figure>
      </Section>

      {/* 2. Logos */}
      <Section tone="plain" labelledBy="logos">
        <h2 id="logos" className={mergeClasses(s.h2, s.centre)} style={{ fontSize: '1.15rem' }}>
          {logos.heading}
        </h2>
        <ul className={s.logos}>
          {logos.items.map((l) => (
            <li key={l.name}>
              {l.src ? (
                <img className={s.logo} src={l.src} alt={l.name} height={40} loading="lazy" decoding="async" />
              ) : (
                <span className={mergeClasses(s.placeholder, s.logoPlaceholder)}>{l.name}</span>
              )}
            </li>
          ))}
        </ul>
      </Section>

      {/* 3. Sprawl */}
      <Section tone="soft" labelledBy="sprawl">
        <h2 id="sprawl" className={s.h2}>
          {sprawl.heading}
        </h2>
        <ol className={mergeClasses(s.grid, s.grid4)} style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
          {sprawl.cards.map((c, i) => (
            <li key={c.title} className={s.card}>
              <span className={s.number} aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className={s.h3}>{c.title}</h3>
              <p className={s.p}>{c.text}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* 4. Features */}
      <Section tone="plain" labelledBy="features">
        <h2 id="features" className={s.h2}>
          {features.heading}
        </h2>
        <div className={mergeClasses(s.grid, s.grid3)}>
          {features.items.map((f) => (
            <div key={f.title} className={s.card}>
              <span className={s.cardIcon} aria-hidden="true">
                {ICONS[f.icon]}
              </span>
              <h3 className={s.h3}>{f.title}</h3>
              <p className={s.p}>{fill(f.text)}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* 5. Data flow */}
      <Section tone="tint" labelledBy="dataflow">
        <h2 id="dataflow" className={s.h2}>
          {dataFlow.heading}
        </h2>
        <DataFlowDiagram boxes={dataFlow.boxes} label={dataFlow.diagramLabel} />
        <div className={s.blocks}>
          {dataFlow.blocks.map((b) => (
            <div key={b.title} className={s.block}>
              <span className={s.cardIcon} aria-hidden="true">
                {ICONS[b.icon]}
              </span>
              <div style={{ display: 'grid', gap: '6px' }}>
                <h3 className={s.h3}>{b.title}</h3>
                <p className={s.p}>{fill(b.text)}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 6. Comparison */}
      <Section tone="plain" labelledBy="compare">
        <div style={{ display: 'grid', gap: '10px' }}>
          <h2 id="compare" className={s.h2}>
            {comparison.heading}
          </h2>
          <p className={s.intro}>{comparison.intro}</p>
        </div>
        <div style={{ display: 'grid', gap: '6px' }}>
          <p className={s.scrollHint} aria-hidden="true">
            Swipe the table sideways to see every column.
          </p>
          <div className={s.tableWrap} role="region" aria-label={`${comparison.heading}: table`} tabIndex={0}>
            <table className={s.table}>
              <thead>
                <tr>
                  {comparison.columns.map((c, i) => (
                    <th key={c} scope="col" className={mergeClasses(s.th, i === comparison.columns.length - 1 && s.ours)}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comparison.rows.map(([need, ...cells]) => (
                  <tr key={need}>
                    <th scope="row" className={mergeClasses(s.td, s.rowHead)}>
                      {need}
                    </th>
                    {cells.map((c, i) => (
                      <td key={i} className={mergeClasses(s.td, i === cells.length - 1 && s.ours)}>
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* 7. Steps */}
      <Section tone="navy" labelledBy="steps">
        <h2 id="steps" className={mergeClasses(s.h2, s.h2Light)}>
          {steps.heading}
        </h2>
        <ol className={mergeClasses(s.grid, s.grid3)} style={{ listStyleType: 'none', margin: 0, padding: 0 }}>
          {steps.items.map((st, i) => (
            <li key={st.title} className={s.stepCard}>
              <span className={s.stepNumber} aria-hidden="true">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className={s.stepTitle}>{st.title}</h3>
              <p className={s.stepText}>{st.text}</p>
            </li>
          ))}
        </ol>
        {claim(steps.button, s.stepCta)}
      </Section>

      {/* 8. FAQ */}
      <Section tone="soft" labelledBy="faq">
        <h2 id="faq" className={s.h2}>
          {faq.heading}
        </h2>
        <Accordion multiple collapsible className={s.faq}>
          {faq.items.map((item, i) => (
            <AccordionItem key={item.q} value={i} className={s.faqItem}>
              <AccordionHeader as="h3" className={s.faqHeader} expandIconPosition="end">
                {item.q}
              </AccordionHeader>
              <AccordionPanel>
                <p className={s.faqAnswer}>{fill(item.a)}</p>
              </AccordionPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </Section>

      {/* 9. Footer */}
      <footer className={s.footer}>
        <div className={s.footerInner}>
          <span>
            {footer.founderName || <span className={s.missing}>[Founder name]</span>}, {footer.founderTitle}
          </span>
          <span className={s.dot} aria-hidden="true">
            ·
          </span>
          {footer.mobile ? (
            <Link className={s.footerLink} href={telHref(footer.mobile)}>
              {footer.mobile}
            </Link>
          ) : (
            <span className={s.missing}>[mobile]</span>
          )}
          <span className={s.dot} aria-hidden="true">
            ·
          </span>
          <Link className={s.footerLink} href={`mailto:${footer.email}`}>
            {footer.email}
          </Link>
          <span className={s.dot} aria-hidden="true">
            ·
          </span>
          <Link className={s.footerLink} href={footer.privacyUrl} rel="noopener">
            {footer.privacyLabel}
          </Link>
        </div>
      </footer>
    </>
  );
}
